/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography } from '@zapier/design-system';

type Props = {
  /**
   * Array of links to render.
   */
  links: Array<{ text: string; href?: string; className?: string; style?: {} }>;
};

// See note in `HelpfulLinks.md` for context about this file's `CssVars`,
// since it defines some ,too.
export const CssVars = {
  justifyContent: '--UniversalFooter__HelpfulLinks-justify-content',
};

const Styles = {
  root: css`
    ${Typography.altText900};
    color: ${Colors.grey700};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: var(${CssVars.justifyContent}, center);
  `,
  copyright: css`
    margin-right: 10px;
  `,
  links: css`
    display: inherit;
    align-items: inherit;
    justify-content: inherit;
    white-space: nowrap;
  `,
  linkItem: css`
    border-left: 1px solid ${Colors.grey200};
    &:first-of-type {
      border-left: none;
    }
  `,
  link: css`
    // Increase hit area and separate.
    padding: 5px 10px;
    color: inherit;
    &:hover,
    &:focus {
      color: ${Colors.blue500};
    }
  `,
};

/**
 * @private
 * Meta information about Zapier, like copyright info, legal stuff, etc.
 */
export const ZapierMeta = (props: Props) => {
  const linkNodes = props.links.map((link) => {
    const content = link.href ? (
      <Link css={Styles.link} color={null} href={link.href}>
        {link.text}
      </Link>
    ) : (
      <button
        className={link.className}
        css={Styles.link}
        style={link.style}
        type="button"
      >
        {link.text}
      </button>
    );
    return (
      <li key={link.text} css={Styles.linkItem}>
        {content}
      </li>
    );
  });
  return (
    <div css={Styles.root}>
      <p css={Styles.copyright}>© {new Date().getFullYear()} Zapier Inc.</p>
      <ul css={Styles.links}>{linkNodes}</ul>
    </div>
  );
};

ZapierMeta.defaultProps = {
  links: [
    // This `className` is defined by OneTrust and works as a hook to
    // open the cookie preferences modal. The `style` hides the link until
    // OneTrust initializes so that the button only renders once it'll do something.
    {
      text: 'Manage cookies',
      className: 'ot-sdk-show-settings',
      style: { display: 'none' },
    },
    { text: 'Legal', href: '/legal' },
    { text: 'Privacy', href: '/privacy' },
  ],
};
